import React, { useState, useEffect } from 'react';
import { RhLogo } from './RhLogo';
import { subscribeToSiteSettingsFromFirestore, getLocalSiteSettings, SiteSettings } from '../lib/siteSettingsService';
import {
  Layers,
  ShieldCheck,
  Award,
  Send,
  Heart,
  Globe,
  Lock,
  CheckCircle2,
  Sparkles,
  Zap,
  Download,
  CreditCard,
  Headphones,
  Mail,
  Check
} from 'lucide-react';

import { CMSContent } from '../types';
import { RequestQuoteModal } from './RequestQuoteModal';

interface FooterProps {
  darkMode: boolean;
  cms?: CMSContent;
  onOpenAdmin?: () => void;
  onSelectCategorySearch?: (term: string) => void;
  onSelectFormatSearch?: (fmt: string) => void;
  onOpenLegalModal?: (title: string, text: string) => void;
  onOpenSupport?: () => void;
}

export const Footer: React.FC<FooterProps> = ({
  darkMode,
  cms,
  onOpenAdmin,
  onSelectCategorySearch,
  onSelectFormatSearch,
  onOpenLegalModal,
  onOpenSupport,
}) => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(getLocalSiteSettings());

  useEffect(() => {
    const unsub = subscribeToSiteSettingsFromFirestore((settings) => {
      setSiteSettings(settings);
    });
    return () => unsub();
  }, []);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setIsQuoteModalOpen(true);
    }
  };

  return (
    <footer className={`border-t relative z-10 transition-all duration-300 ${
      darkMode ? 'bg-slate-950 border-white/10 text-white' : 'bg-slate-900 border-slate-800 text-slate-100'
    }`}>
      <div className="max-w-[1200px] w-full mx-auto px-5 sm:px-8 md:px-10 lg:px-12 pt-12 sm:pt-16 pb-24 lg:pb-12 space-y-10 sm:space-y-12">
        
        {/* Top Newsletter & Guarantee Row */}
        {siteSettings.newsletterEnabled !== false && (
          <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 p-5 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl glass-card border border-cyan-500/30 shadow-2xl relative overflow-hidden">
            {/* Subtle Ambient Glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="lg:col-span-7 space-y-2.5 sm:space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
                <span className="text-[11px] sm:text-xs font-extrabold uppercase tracking-wider text-cyan-400">
                  {siteSettings.newsletterBadgeText || 'Join 35,000+ Designers & Professionals'}
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-black leading-tight">
                {siteSettings.newsletterHeading || 'Get 20% Off Your First 3D Master Template Download'}
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 max-w-xl leading-relaxed">
                {siteSettings.newsletterDescription || 'Subscribe to the RH Editography VIP briefing for weekly 300 DPI vector releases, security microprint templates, and instant discount passes.'}
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col justify-center">
              {subscribed ? (
                <div className="p-4 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold flex items-center gap-2.5 shadow-lg">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-400" />
                  <span>Subscribed! Use pass code <span className="font-mono underline text-white px-1 bg-emerald-950/60 rounded">{siteSettings.newsletterSuccessDiscountCode || 'RH3D20'}</span> at checkout for {siteSettings.newsletterDiscountPercentage || '20%'} off.</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2.5 sm:gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address..."
                    className="w-full flex-1 px-4 py-3 sm:py-3.5 rounded-2xl bg-slate-900/90 border border-slate-700 text-xs font-bold focus:outline-none focus:border-cyan-400 text-white placeholder-slate-400 shadow-inner"
                  />
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-3 sm:py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 text-white font-extrabold text-xs shadow-lg shadow-blue-500/25 hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 flex-shrink-0"
                  >
                    <Send className="w-4 h-4" />
                    <span>{siteSettings.newsletterButtonText || 'Get VIP Pass'}</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Links Navigation Grid (Responsive for Mobile, Tablet, and Desktop) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-8 text-xs">
          
          {/* Brand Info Column */}
          <div className="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-2 space-y-4 lg:space-y-3.5">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 aspect-square flex items-center justify-center overflow-hidden rounded-xl">
                {siteSettings.siteLogoFooterUrl || siteSettings.siteLogoHeaderUrl ? (
                  <img
                    src={siteSettings.siteLogoFooterUrl || siteSettings.siteLogoHeaderUrl}
                    alt={siteSettings.siteTitle || 'RH Editography'}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <RhLogo className="w-full h-full" />
                )}
              </div>
              <div>
                <a href="https://rheditography.net" className="font-extrabold text-lg sm:text-xl text-gradient hover:opacity-90">
                  {siteSettings.siteTitle || 'RH Editography'}
                </a>
                <p className="text-[10px] text-cyan-400 font-semibold uppercase tracking-wider">
                  {siteSettings.siteTaglineFooter || '3D Digital Vector Marketplace'}
                </p>
              </div>
            </div>
            
            <p className="text-slate-400 leading-relaxed max-w-md text-xs">
              {siteSettings?.footerDescription || 'World-class 3D digital marketplace specialized in editable vector document templates, biometric passports, ID cards, diplomas, financial statements, and corporate agreements.'}
            </p>

            <div className="flex gap-2 items-center pt-1">
              {siteSettings?.socialInstagramUrl && (
                <a
                  href={siteSettings.socialInstagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[32px] h-[32px] rounded-[10px] border border-slate-700/60 bg-[#161a25] hover:bg-slate-800 hover:border-cyan-500/50 hover:text-cyan-400 transition-all flex items-center justify-center text-slate-400"
                >
                  <svg viewBox="0 0 24 24" className="w-[14px] h-[14px]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="4" width="16" height="16" rx="4" ry="4"></rect>
                    <circle cx="12" cy="12" r="3"></circle>
                    <line x1="16.5" y1="7.5" x2="16.5" y2="7.501"></line>
                  </svg>
                </a>
              )}
              {siteSettings?.socialWhatsappUrl && (
                <a
                  href={siteSettings.socialWhatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[32px] h-[32px] rounded-[10px] border border-slate-700/60 bg-[#161a25] hover:bg-slate-800 hover:border-cyan-500/50 hover:text-cyan-400 transition-all flex items-center justify-center text-slate-400"
                >
                  <svg viewBox="0 0 24 24" className="w-[14px] h-[14px]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21l1.65 -3.8a9 9 0 1 1 3.4 2.9l-5.05 .9"></path>
                    <path d="M9 10a.5 .5 0 0 0 1 0v-1a.5 .5 0 0 0 -1 0v1a5 5 0 0 0 5 5h1a.5 .5 0 0 0 0 -1h-1a.5 .5 0 0 0 0 1"></path>
                  </svg>
                </a>
              )}
              {siteSettings?.socialTelegramUrl && (
                <a
                  href={siteSettings.socialTelegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[32px] h-[32px] rounded-[10px] border border-slate-700/60 bg-[#161a25] hover:bg-slate-800 hover:border-cyan-500/50 hover:text-cyan-400 transition-all flex items-center justify-center text-slate-400"
                >
                  <svg viewBox="0 0 24 24" className="w-[14px] h-[14px]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 10l-4 4l6 6l4 -16l-18 7l4 2l2 6l3 -4"></path>
                  </svg>
                </a>
              )}
              {siteSettings?.socialDribbbleUrl && (
                <a
                  href={siteSettings.socialDribbbleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[32px] h-[32px] rounded-[10px] border border-slate-700/60 bg-[#161a25] hover:bg-slate-800 hover:border-cyan-500/50 hover:text-cyan-400 transition-all flex items-center justify-center text-slate-400"
                >
                  <svg viewBox="0 0 24 24" className="w-[14px] h-[14px]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9"></circle>
                    <path d="M9 3.6c5 6 7 10.5 7.5 16.2"></path>
                    <path d="M6.4 19c3.5 -3.5 6 -6.5 14.5 -6.4"></path>
                    <path d="M3.1 10.75c5 0 9.814 -.38 15.314 -5"></path>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Column 2: Popular Categories */}
          <div className="space-y-2.5">
            <h4 className="font-extrabold uppercase text-slate-200 tracking-wider text-xs border-b border-slate-800/80 pb-2 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
              <span>Popular Categories</span>
            </h4>
            <ul className="space-y-1.5 text-slate-400 font-medium">
              <li onClick={() => onSelectCategorySearch?.('Passport')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <Check className="w-3 h-3 text-cyan-500/60" /> Passport
              </li>
              <li onClick={() => onSelectCategorySearch?.('ID Card')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <Check className="w-3 h-3 text-cyan-500/60" /> ID Card
              </li>
              <li onClick={() => onSelectCategorySearch?.('Driving License')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <Check className="w-3 h-3 text-cyan-500/60" /> Driving Licence
              </li>
              <li onClick={() => onSelectCategorySearch?.('Certificate')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <Check className="w-3 h-3 text-cyan-500/60" /> Certificate
              </li>
              <li onClick={() => onSelectCategorySearch?.('Bank Statement')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <Check className="w-3 h-3 text-cyan-500/60" /> Bank Statement
              </li>
              <li onClick={() => { onSelectCategorySearch?.(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="hover:text-cyan-400 cursor-pointer transition-colors pt-3 mt-2 border-t border-slate-800/80 flex items-center text-sm font-bold text-cyan-500/90 w-full text-left">
                View All Categories <span className="ml-1">&rarr;</span>
              </li>
            </ul>
          </div>

          {/* Column 3: Formats & Tools */}
          <div className="space-y-2.5">
            <h4 className="font-extrabold uppercase text-slate-200 tracking-wider text-xs border-b border-slate-800/80 pb-2 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-purple-400"></span>
              <span>Formats & Tools</span>
            </h4>
            <ul className="space-y-1.5 text-slate-400 font-medium">
              <li onClick={() => onSelectFormatSearch?.('PSD')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 text-cyan-300 rounded font-bold border border-cyan-500/20">PSD</span> Photoshop
              </li>
              <li onClick={() => onSelectFormatSearch?.('AI')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 text-amber-300 rounded font-bold border border-amber-500/20">AI</span> Illustrator
              </li>
              <li onClick={() => onSelectFormatSearch?.('CDR')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 text-emerald-300 rounded font-bold border border-emerald-500/20">CDR</span> CorelDRAW
              </li>
              <li onClick={() => onSelectFormatSearch?.('PDF')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 text-rose-300 rounded font-bold border border-rose-500/20">PDF</span> Vector PDF
              </li>
              <li onClick={() => onSelectFormatSearch?.('EPS')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 flex items-center gap-1.5">
                <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-800 text-purple-300 rounded font-bold border border-purple-500/20">EPS</span> EPS Vector
              </li>
            </ul>
          </div>

          {/* Column 4: Legal & Support */}
          <div className="col-span-1 sm:col-span-2 md:col-span-1 lg:col-span-1 space-y-2.5">
            <h4 className="font-extrabold uppercase text-slate-200 tracking-wider text-xs border-b border-slate-800/80 pb-2 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
              <span>Legal & Support</span>
            </h4>
            <ul className="space-y-1.5 text-slate-400 font-medium">
              <li onClick={() => onOpenLegalModal?.('Terms of Service', 'terms')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5">
                Terms of service
              </li>
              <li onClick={() => onOpenLegalModal?.('Privacy Policy', 'privacy')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5">
                Privacy Policy
              </li>
              <li onClick={() => onOpenLegalModal?.('Refund Policy', 'refund')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5">
                Refund Policy
              </li>
              <li onClick={() => onOpenLegalModal?.('License & IP', 'faq')} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5">
                License & IP Policy
              </li>
              <li onClick={() => onOpenSupport?.()} className="hover:text-cyan-400 cursor-pointer transition-colors py-0.5 font-bold text-cyan-300 flex items-center gap-1">
                <Headphones className="w-3.5 h-3.5 text-cyan-400" /> 24/7 Support Desk
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Copyright & Official Payment Logos */}
        <div className="pt-8 border-t border-slate-800/80 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4 text-xs text-slate-500 font-medium text-center md:text-left">
          <p>
            {(() => {
              const text = siteSettings?.footerCopyright || 'Made by RH Editography © 2024.';
              const parts = text.split('RH Editography');
              if (parts.length > 1) {
                return parts.map((part, i) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < parts.length - 1 && (
                      <a href="https://rheditography.net" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline font-extrabold">
                        RH Editography
                      </a>
                    )}
                  </React.Fragment>
                ));
              }
              return text;
            })()}
          </p>

          <div className="flex flex-wrap items-center justify-center md:justify-end gap-2.5 sm:gap-3 text-[11px] font-bold">
            {/* Bitcoin Logo */}
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center p-1 shadow-md shadow-black/30 border border-slate-700/60 hover:scale-110 transition-transform overflow-hidden" title="Bitcoin (BTC)">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRZnisI3YHfRSyEFthESUmLDOxJAK2_CmIjsz7K-5I1Sw&s=10"
                alt="Bitcoin"
                className="w-full h-full object-contain rounded-full"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </div>

            {/* Tether (USDT) Logo */}
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center p-1 shadow-md shadow-black/30 border border-slate-700/60 hover:scale-110 transition-transform overflow-hidden" title="Tether (USDT)">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQet37hbusRyBPLGCMmGetIset8Ql7UtDnX6jnr_Ekd5Q&s=10"
                alt="Tether"
                className="w-full h-full object-contain rounded-full"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </div>

            {/* Cash App Logo */}
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center p-1 shadow-md shadow-black/30 border border-slate-700/60 hover:scale-110 transition-transform overflow-hidden" title="Cash App">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTNxvzdrZJivcD4zi0oX84LKkb223LmAm1quMMXtFVaPg&s"
                alt="Cash App"
                className="w-full h-full object-contain rounded-full"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </div>

            {/* Chime Logo */}
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center p-1 shadow-md shadow-black/30 border border-slate-700/60 hover:scale-110 transition-transform overflow-hidden" title="Chime">
              <img
                src="https://img.icons8.com/color/512/chime.png"
                alt="Chime"
                className="w-full h-full object-contain rounded-full"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </div>

            {/* Paypal Logo */}
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white flex items-center justify-center p-1 shadow-md shadow-black/30 border border-slate-700/60 hover:scale-110 transition-transform overflow-hidden" title="PayPal">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTvKaMczQAg9mWTKysI4HfF26rFI3Uivzmk3h1q4Vjjyw&s=10"
                alt="Paypal"
                className="w-full h-full object-contain rounded-full"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
        </div>

      </div>
      <RequestQuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
        prefilledEmail={email} 
      />
    </footer>
  );
};

